<template>
  <PrmFileUpload
    :multiple="maxFiles === undefined || maxFiles > 1"
    :max-file-size="maxFileSize"
    :disabled="isUploadDisabled"
    :accept="htmlAccept"
    :file-limit="maxFiles"
    @select="onFileSelect($event.files)"
    @remove="onFileSelect($event.files)"
  >
    <template
      #header="{
        chooseCallback, clearCallback, files,
      }"
    >
      <div
        class="flex-row"
      >
        <QmulusButton
          :label="i18n.global.t(`${I18N_PREFIX}.addFile`)"
          severity="tertiary"
          icon="pi pi-plus"
          :disabled="isUploadDisabled"
          @click="chooseCallback()"
        />
        <QmulusButton
          v-show="files.length"
          :label="i18n.global.t(`${I18N_PREFIX}.removeAllFiles`)"
          icon="pi pi-times"
          severity="tertiary"
          :disabled="disabled"
          @click="clearCallback(); onFileSelect([]);"
        />
      </div>
    </template>
    <template
      #content="{
        files, removeFileCallback,
      }"
    >
      <div
        v-if="files.length > 0"
        class="flex-row"
      >
        <TextFile
          v-for="(file, index) of files"
          :key="file.name + file.type + file.size"
          :file="file"
        >
          <template #actions>
            <SemanticButton
              presentation="inline"
              variant="cancel"
              @click="removeFileCallback(index)"
            />
          </template>
        </TextFile>
      </div>
    </template>
    <template #empty>
      <UploadPlaceholder
        :multiple="isMultipleFilesAllowed"
        :accept="acceptMimeTypeList"
        :max-file-size-bytes="maxFileSize"
      />
    </template>
  </PrmFileUpload>
</template>

<script lang="ts" setup>
import PrmFileUpload from '@NitrexGroup/primevue/fileupload';
import {
  i18n,
} from '@i18n';
import QmulusButton from '@qmulus/ui/components/button/qmulus-button.vue';
import SemanticButton from '@qmulus/ui/components/button/semantic-button.vue';
import {
  computed,
  shallowRef,
} from 'vue';

import TextFile from '@/components/text-file/text-file.vue';
import UploadPlaceholder from '@/components/upload-field/upload-placeholder.vue';
import {
  MAXIMUM_FILE_SIZE,
} from '@/constants/file-upload.ts';
import {
  EMimeType,
} from '@/types/mime.ts';

const props = withDefaults(
  defineProps<{
    /**
     * The accept attribute is a type safe implementation of the accept
     * attribute of the <input type="file">
     * https://developer.mozilla.org/de/docs/Web/HTML/Reference/Elements/input/file#accept
     *
     * If the list is empty of undefined the file types are not restricted
     *
     * This attribute is required, because uploading of arbitrary files is considered a major security risk:
     * https://nitrexgroup.atlassian.net/browse/QMU-4711
     */
    accept: EMimeType | EMimeType[],
    disabled?: boolean,
    /**
     * Allow to limit the maximum amount of files that can be uploaded.
     */
    maxFiles?: number,
    maxFileSize?: number,
  }>(),
  {
    maxFileSize: MAXIMUM_FILE_SIZE,
    maxFiles: undefined,
  },
);

const emit = defineEmits<{
  (e: 'uploadFileList', fileList: File[]): void,
}>();

const I18N_PREFIX = 'components/upload-file-drag-and-drop/upload-file-drag-and-drop';

const selectedFileCnt = shallowRef<number>(0);

const isUploadDisabled = computed<boolean>(() => {
  if (props.disabled) {
    return true;
  }
  if (props.maxFiles !== undefined) {
    return selectedFileCnt.value >= props.maxFiles;
  }
  return false;
});

// https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Attributes/accept#limiting_accepted_file_types
const htmlAccept = computed<string | undefined>(() => {
  if (Array.isArray(props.accept)) {
    return props.accept.join(', ');
  }
  return props.accept;
});

const acceptMimeTypeList = computed<EMimeType[]>(() => {
  if (Array.isArray(props.accept)) {
    return props.accept;
  }
  return [
    props.accept,
  ];
});
const isMultipleFilesAllowed = computed(() => props.maxFiles !== 1);

function onFileSelect(files: File[]) {
  selectedFileCnt.value = files.length;
  emit('uploadFileList', files);
}
</script>
